import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Lock, Database, ScrollText, Server, HeartHandshake, Accessibility, Gavel } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const ICON = {
    security: ShieldCheck, privacy: Lock, data_handling: Database,
    subprocessors: Server, backup: Database, disclosure: HeartHandshake,
    accessibility: Accessibility, legal: Gavel,
};

/**
 * Public Trust Centre — /trust
 *
 * Content comes from /api/trust/content. Only claims backed by real
 * implementation are surfaced (see the endpoint's docstring). No
 * "unhackable" / "bank-grade" language. Renders before login so it's
 * useful for prospective customers evaluating VAULT.
 */
export default function Trust() {
    const [d, setD] = useState(null);
    useEffect(() => {
        const url = process.env.REACT_APP_BACKEND_URL + "/api/trust/content";
        axios.get(url).then((r) => setD(r.data)).catch(() => setD({ sections: [] }));
    }, []);

    return (
        <div className="min-h-screen bg-stone-50 text-stone-900">
            <div className="max-w-5xl mx-auto px-6 pt-10 pb-24">
                <div className="flex items-center justify-between mb-10">
                    <Link to="/"><BrandLogo height={32} /></Link>
                    <Link to="/status" className="text-[12.5px] text-stone-600 hover:text-stone-900" data-testid="trust-to-status">
                        System status →
                    </Link>
                </div>

                <div className="mb-14">
                    <div className="eyebrow text-amber-700">Trust Centre</div>
                    <h1 className="font-display text-5xl sm:text-6xl leading-none mt-3">
                        Your finances,<br />held with care.
                    </h1>
                    <p className="text-base text-stone-600 mt-5 max-w-2xl">
                        A single, honest place to see how VAULT protects your data. Every claim below
                        is backed by real implementation — nothing marketing-only, nothing we cannot show.
                    </p>
                </div>

                {!d ? (
                    <p className="text-sm text-stone-500">Loading…</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {d.sections.map((s) => {
                            const Icon = ICON[s.id] || ScrollText;
                            return (
                                <section
                                    key={s.id}
                                    data-testid={`trust-section-${s.id}`}
                                    className="rounded-2xl border border-stone-200 bg-white p-6"
                                >
                                    <div className="flex items-center gap-2 mb-4">
                                        <Icon size={16} className="text-amber-700" />
                                        <h2 className="text-[16px] font-semibold text-stone-900">{s.title}</h2>
                                    </div>
                                    <ul className="space-y-2.5">
                                        {s.items.map((item, i) => (
                                            <li key={i} className="flex items-start gap-2 text-[13.5px] leading-relaxed text-stone-700">
                                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" />
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            );
                        })}
                    </div>
                )}

                <div className="mt-14 rounded-2xl border border-stone-200 bg-white p-6 flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-[15px] font-semibold text-stone-900">Report a security issue</div>
                        <p className="text-[13px] text-stone-600 mt-1">Please email <a href="mailto:security@vaultbypd.com" className="text-amber-700 underline">security@vaultbypd.com</a>. We acknowledge every report within 3 business days.</p>
                    </div>
                    <Link to="/status" className="text-[13px] font-semibold text-stone-900 hover:text-amber-700">
                        View system status →
                    </Link>
                </div>
            </div>
        </div>
    );
}
